import React, { useEffect, useState } from 'react';
import PageHeading from '../../components/PageHeading';
import { MailOutlined } from '@ant-design/icons';
import {
  Card,
  Checkbox,
  Button,
  Input,
  Row,
  Col,
  message,
  Space,
  Alert
} from 'antd';
import axiosInstance from '../../utils/axios.js';
import { useParams } from 'react-router-dom';
import MDEditor from '@uiw/react-md-editor';
import { marked } from 'marked';

const { TextArea } = Input;

const ShowRemarks = () => {
  const params = useParams();
  const { showId, date, timeslotId } = useParams();
  const [bookedCustomers, setBookedCustomers] = useState([]);
  const [additionalEmails, setAdditionalEmails] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [doneCustomers, setDoneCustomers] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    fetchBookedCustomers();
  }, [showId, date, timeslotId]);

  const fetchBookedCustomers = async () => {
    try {
      const response = await axiosInstance.get(
        `booking/get-customer-emails/${showId}/${date}/${timeslotId}/`
      );
      setBookedCustomers(response.data || []);
    } catch (error) {
      console.error("Error fetching booked customers:", error);
      messageApi.error("Failed to fetch booked customers");
    }
  };

  const handleCustomerSelect = (customerEmail, checked) => {
    if (checked) {
      setSelectedCustomers(prev => [...prev, customerEmail]);
    } else {
      setSelectedCustomers(prev =>
        prev.filter(email => email !== customerEmail)
      );
    }
  };

  const handleAddEmail = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!newEmail.trim()) {
      messageApi.warning("Please enter an email address");
      return;
    }

    if (!emailRegex.test(newEmail)) {
      messageApi.warning("Please enter a valid email address");
      return;
    }

    const allEmails = [...bookedCustomers, ...additionalEmails];
    if (allEmails.includes(newEmail)) {
      messageApi.warning("This email is already in the list");
      return;
    }

    setAdditionalEmails(prev => [...prev, newEmail]);
    setNewEmail('');
    messageApi.success("Email added successfully");
  };

  const handleRemoveAdditionalEmail = (email) => {
    setAdditionalEmails(prev => prev.filter(e => e !== email));
    setSelectedCustomers(prev => prev.filter(e => e !== email));
    messageApi.success("Email removed successfully");
  };

  const handleSendRemarks = async () => {
    if (selectedCustomers.length === 0) {
      messageApi.warning("Please select at least one customer");
      return;
    }

    if (!messageText.trim()) {
      messageApi.warning("Please enter a message");
      return;
    }

    setLoading(true);
    try {
      const htmlMessage = marked(messageText);

      await axiosInstance.post(
        `booking/get-customer-emails/${showId}/${date}/${timeslotId}/`,
        {
          recipients: selectedCustomers,
          remark: htmlMessage
        }
      );

      messageApi.success("Remarks sent successfully");
      setDoneCustomers(prev => [...prev, ...selectedCustomers]);
      setSelectedCustomers([]);
      setMessageText('');
    } catch (error) {
      console.error("Error sending remarks:", error);
      messageApi.error("Failed to send remarks");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard">
      {contextHolder}
      <PageHeading name="Send Remarks" icon={<MailOutlined />} />

      <Alert
        message="Show Details"
        description="You can send email with contents to selected customers here. Please only try with 10 email max at a time."
        type="info"
        showIcon
        style={{ marginBottom: 20 }}
      />

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={8}>
          <Card title="Booked Customers" bordered>

            {/* ADD EMAIL OPTION */}
            <Space direction="vertical" style={{ width: '100%', marginBottom: 12 }}>
              <Input
                placeholder="Enter email address"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onPressEnter={handleAddEmail}
              />
              <Button type="dashed" onClick={handleAddEmail} block>
                Add Email
              </Button>
            </Space>

            <Space direction="vertical" style={{ width: '100%' }}>
              {bookedCustomers.map(email => (
                <Checkbox
                  key={email}
                  onChange={(e) =>
                    handleCustomerSelect(email, e.target.checked)
                  }
                  disabled={doneCustomers.includes(email)}
                >
                  {email}
                </Checkbox>
              ))}

              {additionalEmails.map(email => (
                <div
                  key={email}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <Checkbox
                    onChange={(e) =>
                      handleCustomerSelect(email, e.target.checked)
                    }
                    disabled={doneCustomers.includes(email)}
                  >
                    {email}
                  </Checkbox>

                  <Button
                    type="link"
                    danger
                    onClick={() => handleRemoveAdditionalEmail(email)}
                    style={{ padding: 0 }}
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </Space>
          </Card>
        </Col>

        <Col xs={24} sm={8}>
          <Card title="Selected Customers" bordered>
            <Space direction="vertical" style={{ width: '100%' }}>
              {selectedCustomers.map(email => (
                <div key={email}>{email}</div>
              ))}
            </Space>
          </Card>
        </Col>

        <Col xs={24} sm={8}>
          <Card title="Done" bordered>
            <Space direction="vertical" style={{ width: '100%' }}>
              {doneCustomers.map(email => (
                <div key={email}>{email}</div>
              ))}
            </Space>
          </Card>
        </Col>
      </Row>

      <Card title="Message" style={{ marginTop: 20 }}>
        <MDEditor
          value={messageText}
          onChange={setMessageText}
          preview="edit"
          hideToolbar={false}
          visibleDragBar={false}
          textareaProps={{
            placeholder:
              'Enter your message to send as email to the selected customers'
          }}
        />

        <Button
          type="primary"
          onClick={handleSendRemarks}
          loading={loading}
          style={{ marginTop: 10 }}
        >
          Send Remarks
        </Button>
      </Card>
    </div>
  );
};

export default ShowRemarks;
